import { Calendar, Users, Image, Layers } from 'lucide-react';
import type { ChPost } from '../types';
import { MediaPreview } from './MediaPreview';
import { PostMetricsBadge } from './MetricsDisplay';
import { DeadlineBadge } from './DeadlineBadge';
import { POST_STATUS_LABELS, PLATFORM_LABELS, CONTENT_TYPE_LABELS } from '../types';
import { getAssigneeIds, getCoverUrl, getMediaUrls, getPlatforms } from '../lib/posts';

interface Props {
  post: ChPost;
  onClick: (post: ChPost) => void;
  /** Names of the people responsible, by id. */
  names?: Map<string, string>;
}

export function PostCard({ post, onClick, names }: Props) {
  const cover = getCoverUrl(post);
  const slides = getMediaUrls(post).length;
  const assignees = getAssigneeIds(post);
  const assigneeNames = assignees.map(id => names?.get(id)).filter(Boolean) as string[];

  return (
    <button
      onClick={() => onClick(post)}
      className="ch-card text-left w-full group cursor-pointer"
    >
      <div className="relative w-full aspect-video rounded-xl overflow-hidden mb-3 bg-gray-50 flex items-center justify-center">
        {cover ? (
          <MediaPreview
            url={cover}
            alt={post.title}
            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
          />
        ) : (
          <Image className="w-8 h-8 text-gray-200" />
        )}
        <span className={`ch-badge ch-badge-${post.status} absolute top-2 right-2`}>
          {POST_STATUS_LABELS[post.status]}
        </span>
        {slides > 1 && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-semibold">
            <Layers className="w-3 h-3" />
            {slides}
          </span>
        )}
      </div>

      <h3 className="text-sm font-semibold text-[var(--ch-ink)] truncate mb-1">
        {post.title}
      </h3>

      <div className="flex items-center gap-2 flex-wrap">
        {getPlatforms(post).map(p => (
          <span key={p} className="ch-chip">{PLATFORM_LABELS[p]}</span>
        ))}
        <span className="ch-chip">{CONTENT_TYPE_LABELS[post.content_type]}</span>
        <DeadlineBadge post={post} />
      </div>

      <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
        {post.scheduled_date && (
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {new Date(post.scheduled_date).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })}
          </span>
        )}
        <span className="ml-auto"><PostMetricsBadge metrics={post.metrics} /></span>
        {assignees.length > 0 && (
          <span className="flex items-center gap-1 truncate max-w-[45%]" title={assigneeNames.join(', ')}>
            <Users className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">
              {assigneeNames.length > 0
                ? assigneeNames.map(n => n.split(' ')[0]).join(', ')
                : assignees.length}
            </span>
          </span>
        )}
      </div>
    </button>
  );
}
